import { UserDataAdapter } from "../../presentation/adpters/UserDataAdapter";
import { CompanyContacts } from "../../entity/CompanyContacts";
import { CompanyEntity } from "../../entity/CompanyEntity";
import { ReportSummary } from "../../entity/ReportSummary/ReportSummary";
import { CollectionAdapter } from "../../main/adapters/CollectionAdapter/CollectionAdapter";
import { HttpAdapter } from "../../main/adapters/HttpAdapter";
import { RolesUtils } from "../../utils/roles/roles";
import { nucontersUsers } from "../../utils/users-roles/users-roles";

const companies = new Mongo.Collection("companies", {
    connection: null
});

export class Company extends CollectionAdapter {
    HttpAdapter = new HttpAdapter();
    baseUrl = `${publicSettings.aws.baseUrls.company}/company`;

    constructor({
        User,
        Serie,
        Insight,
        SeriesAccountingAnalytics,
        PointsAccountingAnalytics
    } = {}) {
        super(companies);
        this.User = User;
        this.Serie = Serie;
        this.Insight = Insight;
        this.SeriesAccountingAnalytics = SeriesAccountingAnalytics;
        this.PointsAccountingAnalytics = PointsAccountingAnalytics;
    }

    async createNewCompanies({
        accountingId,
        companies,
        options = {},
        userId = new UserDataAdapter().user()?._id
    }) {
        const response = await this.HttpAdapter.call(
            `${this.baseUrl}/save-many`,
            {
                accountingId,
                userId,
                companies,
                options
            }
        );

        return response;
    }

    async getKpis(accountingId, date) {
        const response = await this.HttpAdapter.call(
            `${this.baseUrl}/get-kpis-by-accountingid`,
            { accountingId, date }
        );

        return response;
    }

    async consultCnpj({ cnpj } = {}) {
        if (!cnpj) return null;
        try {
            const response = await this.HttpAdapter.call(
                `${this.baseUrl}/consult-cnpj`,
                { cnpj }
            );
            return response;
        } catch (error) {
            throw new Error(error.message);
        }
    }

    async getByAccountingId({
        companiesIds = [],
        accountingId,
        withContacts,
        summary,
        user,
        status,
        relation,
        integration
    } = {}) {
        if (!accountingId) return [];
        const input = {
            accountingId,

            filters: {
                ...(status ? { status } : {}),
                ...(companiesIds.length ? { companiesIds } : {}),
                ...(integration ? { integration } : {})
            },
            options: {
                summary,
                withContacts
            }
        };
        const response = await this.HttpAdapter.call(
            `${this.baseUrl}/get-by-accountingid`,
            input
        );
        if (!response) return [];
        if (!user) {
            return response.list.map((company) => {
                const companyEntity = new CompanyEntity(company);
                companyEntity.setNameND();
                companyEntity.setCompanyIdContact();
                return companyEntity;
            });
        }
        const userCompanies = this._filterCompaniesByUserRole(
            response.list,
            user
        );
        return userCompanies.map((company) => {
            const companyEntity = new CompanyEntity(company);
            companyEntity.setNameND();
            companyEntity.setCompanyIdContact();
            if (relation) companyEntity.setOnlyContactsHas(relation);
            return companyEntity;
        });
    }

    async getSummary({ accountingId } = {}) {
        if (!accountingId) return [];
        const input = {
            accountingId,
            options: {
                summary: true
            }
        };
        const response = await this.HttpAdapter.call(
            `${this.baseUrl}/get-by-accountingid`,
            input
        );
        const list = response?.list;
        if (!list?.length) return [];
        return list.map((company) => {
            const companyEntity = new CompanyEntity(company);
            companyEntity.setNameND();
            return companyEntity;
        });
    }

    async getByAccId(input) {
        try {
            const response = await this.HttpAdapter.call(
                `${this.baseUrl}/get-by-accountingid`,
                input
            );
            const list = response?.list || [];
            return list;
        } catch (error) {
            return [];
        }
    }

    async join({ accountingId, companies }) {
        try {
            const response = await this.HttpAdapter.call(
                `${this.baseUrl}/join`,
                {
                    accountingId,
                    companies
                }
            );
            return response;
        } catch (err) {
            console.log("err", err);
        }
    }

    async getGroupByreports({
        accountingId,
        date,
        filter,
        limit,
        page,
        sort,
        searchText,
        lastDays
    } = {}) {
        if (!accountingId) return { list: [], total: 0 };
        const response = await this.HttpAdapter.call(
            `${this.baseUrl}/get-group-by-reports`,
            {
                accountingId,
                date,
                filter,
                limit,
                page,
                sort,
                searchText,
                lastDays
            }
        );
        if (!response) return { list: [], total: 0 };
        const list = response.list.map((company) => {
            const reportsList = [];
            for (const report of company.reports) {
                const reportEntity = new ReportSummary(report);
                reportsList.push(reportEntity);
            }
            company.reports = reportsList;
            const companyEntity = new CompanyEntity(company);
            companyEntity.setNameND();
            return companyEntity;
        });
        return { list, total: response.total };
    }

    async updateScheduleDayIntegration({
        companyId,
        accountingId,
        system,
        day
    } = {}) {
        if (!accountingId || !system || day < 1 || day > 29) return false;
        const response = await this.HttpAdapter.call(
            `${this.baseUrl}/update-schedule-day-integration`,
            { companyId, accountingId, system, day: Number(day) }
        );
        return response;
    }

    async getGroupByContacts({
        accountingId,
        companyId = null,
        type,
        userId,
        options = {}
    }) {
        try {
            const result = await this.HttpAdapter.call(
                `${this.baseUrl}/get-group-by-contacts`,
                {
                    accountingId,
                    companyId,
                    type,
                    userId,
                    options,
                    searchText: options.searchText
                }
            );
            const companies = result.companies.map((companyContacts) => {
                const props = {
                    _id: companyContacts.company?._id,
                    name: companyContacts.company?.name,
                    status: companyContacts.company?.status,
                    createdAt: companyContacts.company?.createdAt,
                    contacts: companyContacts.contacts
                };
                const entity = new CompanyContacts(props);
                entity.transformRelationsToBool();
                return entity;
            });
            return { list: companies, kpis: result?.kpis || {} };
        } catch (err) {
            console.log("err", err);
            return { list: [], kpis: {} };
        }
    }

    async getSegmentInfo({
        accountingId,
        limit = 20,
        searchText = "",
        page = 0,
        filters = {},
        sort = [],
        userId = null,
        role = "",
        seriesConfig
    }) {
        if (!accountingId) return [];
        const options = { limit, page, filters, sort, searchText };
        try {
            const result = await this.HttpAdapter.call(
                `${this.baseUrl}/get-segment-info`,
                {
                    accountingId,
                    options,
                    userId
                }
            );
            const companies = result?.list || [];
            const commonCompanies =
                role === "Empresa" ? [] : result?.listModel || [];
            return {
                companies: companies.map((c) => {
                    const entity = CompanyEntity.restore(c, seriesConfig);
                    return entity;
                }),
                total: result?.total || 0,
                commonCompanies: commonCompanies.map((c) => {
                    const entity = CompanyEntity.restore(c, seriesConfig);
                    return entity;
                })
            };
        } catch (err) {
            console.log("ERR", err);
            return { companies: [], total: 0 };
        }
    }

    async updateFields(accountingId, companyId, fields = {}) {
        if (!companyId || !accountingId || !fields) return false;
        try {
            const result = await this.HttpAdapter.call(
                `${this.baseUrl}/update`,
                {
                    accountingId,
                    companies: [{ companyId, ...fields }]
                }
            );
            return result;
        } catch (err) {
            return err;
        }
    }

    async updateFieldsMany(accountingId, companies) {
        if (!companies?.length) return false;
        try {
            const result = await this.HttpAdapter.call(
                `${this.baseUrl}/update`,
                {
                    accountingId,
                    companies
                }
            );
            return result;
        } catch (err) {
            return err;
        }
    }

    async getByIds(_ids) {
        if (!Array.isArray(_ids)) return [];
        try {
            const response = await this.HttpAdapter.call(
                `${this.baseUrl}/get-by-ids`,
                {
                    ids: _ids
                }
            );
            return response?.list;
        } catch (err) {
            return [];
        }
    }

    async removeMany({ companyIds }) {
        if (!Array.isArray(companyIds)) return false;
        if (!companyIds.length) return false;
        try {
            const response = await this.HttpAdapter.call(
                `${this.baseUrl}/remove-many`,
                {
                    companyIds
                }
            );
            return response === true ? true : false;
        } catch (err) {
            return false;
        }
    }

    createMany(data = [], accountingId) {
        this._before();
        const total = data.length;
        if (!total) return false;
        for (let i = 0; i < total; i++) {
            const document = data[i]?.props;

            if (document._id == "EMPRESA_MODELO") {
                document.accountingId = accountingId;
                document.status = "ATIVO";
            }
            this.create(document);
        }
        return true;
    }

    _filterCompaniesByUserRole(companies, user) {
        if (!user) return companies;
        const roles = [...nucontersUsers, "Contabilidade"];
        const isAdminUser = RolesUtils.userIsInRole({ user, roles });
        if (isAdminUser) return companies;
        const userCompanies = companies.filter((company) => {
            return user.profile.companyIds.includes(company._id);
        });
        return userCompanies;
    }
}
